// src/lib/nav.ts
//
// The navigation IS the permission registry. Every item names the page key it needs;
// the sidebar filters itself to what the signed-in user can reach, which is why one
// application serves the CEO, a technician, a resident and a vendor without any
// role-specific routing.
//
// Page keys here must match src/config/permissionRegistry.ts on the backend.

export interface NavItem {
  label: string;
  path: string;
  page: string;          // permission page key
  icon: string;          // lucide icon name
  /** Only show when the user's data scope is this (e.g. a resident's "My home"). */
  onlyScope?: 'OWN';
  /** Hide when the user's data scope is this (list pages are noise for a single-record scope). */
  hideForScope?: 'OWN';
  /**
   * For a user who can only read this page (no create), show the item only when this
   * probe finds something — a resident's Calendar appears once something is booked at
   * their home. Probes are named here and implemented in AppLayout.
   */
  probe?: 'calendar';
  /** A live count shown beside the label (implemented in AppLayout). */
  badge?: 'messages';
  /** Hide from anyone holding this permission (an agent's "My invoices" is not Finance's page). */
  hideWith?: string;
  /** Show only to someone holding this permission (the other half of a `hideWith` pair). */
  onlyWith?: string;
  /** A page with tabs from several permission pages shows if the user can open any of them. */
  alsoPages?: string[];
  /** Like `alsoPages`, but not for a single-record (OWN) scope, whose page hides that sub-tab (a resident's residents.read). */
  alsoPagesNotOwn?: string[];
  /** Other path prefixes that light this item: detail pages that live under it (a listing, a tenancy under Lettings). */
  matches?: string[];
}

export interface NavGroup {
  group: string;
  items: NavItem[];
  /** Show the group heading even when it holds one page (Marketing › Media, with more Marketing pages to come). */
  keepGroup?: boolean;
  /**
   * The whole sidebar of a Lettings-only member of staff (isLettingsDesk in lib/lettings.ts: reservations.update without
   * dashboard.read — the Letting Accelerator since 3 Nov 2026). Their role reads developments, flats, contacts, media and
   * tenancies because the Lettings pages need them, but those pages are not theirs to browse, so only groups marked
   * here show (Messages stays in the header).
   */
  lettingsDesk?: boolean;
}

export const NAVIGATION: NavGroup[] = [
  {
    group: 'Home',
    items: [
      { label: 'Dashboard', path: '/app', page: 'dashboard', icon: 'LayoutDashboard' },
      // A resident's calendar (and anyone else's without My Day); staff find Calendar under Managers.
      { label: 'Calendar', path: '/app/calendar', page: 'calendar', icon: 'CalendarDays', probe: 'calendar', hideWith: 'myDay.read' },
      { label: 'My home', path: '/app/my-home', page: 'residents', icon: 'Home', onlyScope: 'OWN' },
      { label: 'Rent', path: '/app/my-home/rent', page: 'residents', icon: 'ReceiptText', onlyScope: 'OWN', hideWith: 'myDay.read' },
      { label: 'Repairs', path: '/app/my-home/repairs', page: 'residents', icon: 'Wrench', onlyScope: 'OWN', hideWith: 'myDay.read' },
      { label: 'Documents', path: '/app/my-home/documents', page: 'residents', icon: 'FileText', onlyScope: 'OWN', hideWith: 'myDay.read' },
      { label: 'Messages', path: '/app/messages', page: 'messages', icon: 'MessagesSquare', onlyScope: 'OWN', badge: 'messages' },
      { label: 'My building', path: '/app/my-home/building', page: 'residents', icon: 'Building2', onlyScope: 'OWN', hideWith: 'myDay.read' },
      // Someone who signed up in the app to find a home (the Applicant role): their booking, and the homes on the website.
      { label: 'My booking', path: '/app/my-booking', page: 'applicantPortal', icon: 'KeyRound', onlyScope: 'OWN' },
      { label: 'Find a home', path: '/rent', page: 'applicantPortal', icon: 'Search', onlyScope: 'OWN' },
      // An outside agent's home (phase 3): their referrals, what is waiting on whom, their fees. Staff have Progress instead.
      { label: 'Dashboard', path: '/app/agent', page: 'reservations', icon: 'LayoutDashboard', hideWith: 'reservations.update' },
      { label: 'My invoices', path: '/app/agent-bills', page: 'agentBills', icon: 'Receipt', hideWith: 'bills.read' },
      // Task U: a contractor's own insurance and accreditations (staff check them on the vendor's page).
      { label: 'My documents', path: '/app/my-documents', page: 'contractorPortal', icon: 'ShieldCheck', onlyScope: 'OWN', hideWith: 'myDay.read' },
    ],
  },
  {
    group: 'Finance',
    items: [
      { label: 'Invoices', path: '/app/invoices', page: 'invoices', icon: 'ReceiptText' },
      { label: 'Bills', path: '/app/bills', page: 'bills', icon: 'FileInput' },
      { label: 'Items', path: '/app/items', page: 'items', icon: 'Tags' },
      { label: 'Property funds', path: '/app/property-funds', page: 'propertyFunds', icon: 'Wallet' },
      { label: 'Fees', path: '/app/fees', page: 'fees', icon: 'Percent' },
      { label: 'Transactions', path: '/app/transactions', page: 'payments', icon: 'Banknote' },
      { label: 'Banking', path: '/app/banking', page: 'banking', icon: 'Landmark' },
      { label: 'Arrears', path: '/app/arrears', page: 'arrears', icon: 'AlarmClock' },
      { label: 'Statements', path: '/app/statements', page: 'glReports', icon: 'FileBarChart' },
      { label: 'Chart of accounts', path: '/app/accounts', page: 'chartOfAccounts', icon: 'ListTree' },
      { label: 'Journals', path: '/app/journals', page: 'journals', icon: 'BookOpen' },
      { label: 'Periods', path: '/app/periods', page: 'periods', icon: 'CalendarRange' },
    ],
  },
  {
    group: 'Managers',
    items: [
      // The client demo's top bar: Developments · My day · Building · Property · Accounts · Calendar · Contacts · Reports
      // (Lettings is its own group below since 3 Nov 2026). Messages sits in the header beside the bell (AppLayout); a resident keeps it under Home.
      { label: 'Developments', path: '/app/properties', page: 'properties', icon: 'Building2', alsoPages: ['units', 'reports'] },
      { label: 'My Day', path: '/app/my-day', page: 'myDay', icon: 'ListChecks' },
      // Every development's Building management at once (pages/Building.tsx): Overview, Plant, Contracts, Budgets.
      // An asset's own page lights it too.
      { label: 'Building', path: '/app/building', page: 'buildingRecord', icon: 'Building', alsoPages: ['assets', 'contracts', 'budgets'], matches: ['/app/assets/'] },
      // Every development's Property management, and what was the Maintenance group (pages/Property.tsx): Overview,
      // Works, Certificates, Visits, Tenancies, Changeovers (Works is titled "My jobs" for a single-record scope). A job's
      // page and a tenancy's page light it.
      { label: 'Property', path: '/app/property', page: 'workOrders', icon: 'Wrench', alsoPages: ['compliance', 'inspections', 'tenancies', 'changeovers'], matches: ['/app/work-orders/', '/app/tenancies/'] },
            // The manager's money view (pages/Money.tsx): Overview (fees or budgets) and Deposits. Finance keeps its own group;
      // the chart of accounts keeps /app/accounts.
      { label: 'Accounts', path: '/app/money', page: 'fees', icon: 'PoundSterling', alsoPages: ['budgets', 'deposits'], matches: ['/app/deposits'] },
      { label: 'Calendar', path: '/app/calendar', page: 'calendar', icon: 'CalendarDays', probe: 'calendar', onlyWith: 'myDay.read' },
      // Contractors, everyone else in the address book, and residents (pages/Contacts.tsx). A vendor's page lights it.
      { label: 'Contacts', path: '/app/contacts', page: 'vendors', icon: 'Contact', alsoPages: ['contacts'], alsoPagesNotOwn: ['residents'], matches: ['/app/vendors/'] },
      { label: 'Reports', path: '/app/reports', page: 'reports', icon: 'ChartBar' },
    ],
  },
  {
    // Lettings as its own group (3 Nov 2026, docs/plans/lettings-ui-simplification.md ask 7): one page each, no tab row
    // (pages/Lettings.tsx). Each item shows to anyone who can open one of its permission pages — the same lists the routes
    // gate on (App.tsx). Listings is under Marketing (2026-10-26). An Applications drawer (?flat=) stays on its page.
    group: 'Lettings',
    keepGroup: true,
    lettingsDesk: true,
    items: [
      { label: 'Viewings', path: '/app/lettings/viewings', page: 'viewings', icon: 'CalendarCheck', alsoPages: ['leads'] },
      { label: 'Applications', path: '/app/lettings/applications', page: 'applications', icon: 'ClipboardList', alsoPages: ['reservations'] },
      { label: 'Available', path: '/app/lettings/available', page: 'reservations', icon: 'DoorOpen', alsoPages: ['listings'] },
      { label: 'Offers', path: '/app/lettings/offers', page: 'offers', icon: 'BadgePercent' },
    ],
  },
  {
    // The media library (docs/media-library.md): every building and flat image, floorplan, EPC and video, in folders
    // the website and the app read from. Listings: every advert (moved from Lettings, 2026-10-26); a listing's own page
    // lights it.
    group: 'Marketing',
    keepGroup: true,
    items: [
      { label: 'Media', path: '/app/marketing/media', page: 'media', icon: 'Images' },
      { label: 'Listings', path: '/app/marketing/listings', page: 'listings', icon: 'Megaphone', matches: ['/app/listings/'] },
    ],
  },
  {
    group: 'Ownership',
    items: [
      { label: 'Investors', path: '/app/investors', page: 'investors', icon: 'HandCoins' },
      { label: 'Companies', path: '/app/entities', page: 'entities', icon: 'Landmark' },
    ],
  },
  {
    group: 'Settings',
    items: [
      { label: 'Users', path: '/app/users', page: 'users', icon: 'UserCog' },
      { label: 'Outbox', path: '/app/outbox', page: 'outbox', icon: 'Mail' },
      { label: 'Templates', path: '/app/templates', page: 'templates', icon: 'FileText' },
      { label: 'Tax rates', path: '/app/tax-rates', page: 'taxRates', icon: 'Percent' },
      { label: 'Integrations', path: '/app/integrations', page: 'integrations', icon: 'PlugZap' },
      { label: 'Imports', path: '/app/imports', page: 'imports', icon: 'Upload' },
      { label: 'Audit log', path: '/app/audit', page: 'auditLog', icon: 'ScrollText' },
    ],
  },
];
